import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import { useUpdateVanQuestionnaireStore } from '@/stores/useUpdateVanQuestionnaire';
import {
	ExpectedProfitForNewVehicles as ExpectedProfitForNewVehiclesType,
	Maybe
} from '@/graphql/__generated__/van-credits';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface ExpectedProfitNewVehiclesProps {
	data: Maybe<ExpectedProfitForNewVehiclesType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	dailyFareIncome = 'daily_fare_income',
	dailyGasExpenses = 'daily_gas_expenses',
	dailyDriverSalary = 'daily_driver_salary',
	monthlyMaintenanceExpenses = 'monthly_maintenance_expenses',
	monthlyNetProfit = 'monthly_net_profit',
	monthlyVehicleRent = 'monthly_vehicle_rent'
}

const ExpectedProfitForNewVehicles: React.FC<ExpectedProfitNewVehiclesProps> = ({
	data,
	title,
	isEditing,
	inputErrors
}) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');
	const { expectedProfitForNewVehicles, updateExpectedProfitForNewVehicles } =
		useUpdateVanQuestionnaireStore();
	const [formData, setFormData] = useState(
		expectedProfitForNewVehicles as ExpectedProfitForNewVehiclesType
	);

	useEffect(() => {
		setFormData(expectedProfitForNewVehicles);
	}, [expectedProfitForNewVehicles]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateExpectedProfitForNewVehicles({ [key]: value });
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'dailyFareIncome'}
						value={formData['dailyFareIncome'] || data?.dailyFareIncome || ''}
						onChange={(newValue) => handleChange('dailyFareIncome', newValue)}
						error={inputErrors?.[inputErrorMapping.dailyFareIncome] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'dailyDriverSalary'}
						value={formData['dailyDriverSalary'] || data?.dailyDriverSalary || ''}
						onChange={(newValue) => handleChange('dailyDriverSalary', newValue)}
						error={inputErrors?.[inputErrorMapping.dailyDriverSalary] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'dailyGasExpenses'}
						value={formData['dailyGasExpenses'] || data?.dailyGasExpenses || ''}
						onChange={(newValue) => handleChange('dailyGasExpenses', newValue)}
						error={inputErrors?.[inputErrorMapping.dailyGasExpenses] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'monthlyMaintenanceExpenses'}
						value={formData['monthlyMaintenanceExpenses'] || data?.monthlyMaintenanceExpenses || ''}
						onChange={(newValue) => handleChange('monthlyMaintenanceExpenses', newValue)}
						error={inputErrors?.[inputErrorMapping.monthlyMaintenanceExpenses] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'monthlyNetProfit'}
						value={formData['monthlyNetProfit'] || data?.monthlyNetProfit || ''}
						onChange={(newValue) => handleChange('monthlyNetProfit', newValue)}
						error={inputErrors?.[inputErrorMapping.monthlyNetProfit] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'monthlyVehicleRent'}
						value={formData['monthlyVehicleRent'] || data?.monthlyVehicleRent || ''}
						onChange={(newValue) => handleChange('monthlyVehicleRent', newValue)}
						error={inputErrors?.[inputErrorMapping.monthlyVehicleRent] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			<div className="flex flex-row w-full">
				<div className="flex flex-row flex-wrap w-full">
					{isEditing ? (
						editableFields()
					) : (
						<>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'dailyDriverSalary'}
									value={formatValue(data?.dailyDriverSalary, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'dailyFareIncome'}
									value={formatValue(data?.dailyFareIncome, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'dailyGasExpenses'}
									value={formatValue(data?.dailyGasExpenses, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'monthlyMaintenanceExpenses'}
									value={formatValue(data?.monthlyMaintenanceExpenses, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'monthlyNetProfit'}
									value={formatValue(data?.monthlyNetProfit, t)}
								/>
							</div>
							<div className="w-1/2 px-3 py-1">
								<QuestionnaireInput
									title={'monthlyVehicleRent'}
									value={formatValue(data?.monthlyVehicleRent, t)}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default ExpectedProfitForNewVehicles;
